"use client";

import { getPersonalizeText } from "@/lib/personalize-store";
import { useMemo, useState } from "react";
import { startCheckoutCall } from "@/lib/backend";
import { Cta } from "@/components/marketing/Cta";
import { ASPECT_LABELS, sortByAspect } from "@/lib/wall-art-aspects";
import { useInsiderPassStatus } from "@/lib/use-insider-pass-status";
import { CrossSellModal, type CrossSellSibling } from "./CrossSellModal";
import { PriceWithAnchor } from "@/components/marketing/PriceWithAnchor";
import { MemberPrice } from "@/components/pass/MemberPrice";
import { CheckoutDisclosure } from "@/components/legal/CheckoutDisclosure";

/* /digital-art/[slug] hero — combined hero image + size selector + email
 * + Stripe Buy button. All client-side because the hero image swaps when
 * the buyer picks a different size pill (each size is a different family
 * sibling row in shop_products).
 *
 * Wall art uses /api/shop/checkout (NOT /api/v1/checkout/start which is
 * for templates). startCheckoutCall handles that endpoint. The buyer's
 * high-res print file is emailed by apex-flow-labs after webhook fires. */

export type Sibling = {
  id: string;
  slug: string;
  ratio: string; // "1x1", "4x5", etc
  cover: string;
  priceCents: number;
};

export function WallArtHeroBuy({
  siblings,
  initialSlug,
  title,
  subtitle,
}: {
  siblings: Sibling[];
  initialSlug: string;
  title: string;
  subtitle?: string;
}) {
  const sorted = useMemo(
    () => sortByAspect(siblings).filter((s) => ASPECT_LABELS[s.ratio]),
    [siblings],
  );

  const [activeSlug, setActiveSlug] = useState(initialSlug);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCrossSell, setShowCrossSell] = useState(false);
  /* If the buyer types their email and the backend says they have an
   * active Insider Pass, swap the Buy button for an Open-in-Library
   * link. We don't pre-fetch on every page load — only after they've
   * typed the email so we don't spam the status endpoint. */
  const trimmedEmail = email.trim().toLowerCase();
  const { status: pass } = useInsiderPassStatus(
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail) ? trimmedEmail : null,
  );

  const active =
    sorted.find((s) => s.slug === activeSlug) ?? sorted[0] ?? null;
  const activeInfo = active ? ASPECT_LABELS[active.ratio] : undefined;

  /* Cross-sell candidate: a different size from the same family. We
   * pick the next sibling in the sorted list so the suggestion always
   * complements (e.g. square + portrait, vs. two squares). null when
   * the buyer is on a single-size product. */
  const bumpSibling: CrossSellSibling | null = useMemo(() => {
    if (!active || sorted.length < 2) return null;
    const idx = sorted.findIndex((s) => s.slug === active.slug);
    const next = sorted[(idx + 1) % sorted.length];
    if (!next || next.slug === active.slug) return null;
    const info = ASPECT_LABELS[next.ratio];
    return {
      id: next.id,
      slug: next.slug,
      label: info?.label ?? next.ratio,
      printSize: info?.printSize ?? "",
      priceCents: next.priceCents,
    };
  }, [active, sorted]);

  const goToStripe = async (items: { id: string; quantity: number }[]) => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await startCheckoutCall({
        items: items.map((i) => ({ kind: "product", ...i, text: getPersonalizeText() || undefined })),
        successPath: `/thanks?slug=${active.slug}&session={CHECKOUT_SESSION_ID}`,
        cancelPath: `/digital-art/${active.slug}`,
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout unavailable.");
      setBusy(false);
    }
  };

  /* Buy click — if there's a sibling to cross-sell AND we haven't
   * already shown the modal in this click, show it. Otherwise go
   * straight to Stripe. */
  const onBuy = () => {
    if (!active || busy) return;
    if (bumpSibling) {
      setShowCrossSell(true);
      return;
    }
    void goToStripe([{ id: active.id, quantity: 1 }]);
  };

  const onAcceptBundle = () => {
    if (!active || !bumpSibling) return;
    setShowCrossSell(false);
    void goToStripe([
      { id: active.id, quantity: 1 },
      { id: bumpSibling.id, quantity: 1 },
    ]);
  };

  const onDeclineBundle = () => {
    if (!active) return;
    setShowCrossSell(false);
    void goToStripe([{ id: active.id, quantity: 1 }]);
  };

  if (!active) {
    return (
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div
          className="md:col-span-7 grid place-items-center rounded-2xl border"
          style={{
            aspectRatio: "4 / 5",
            borderColor: "var(--color-border-default)",
            background: "var(--color-bg-elev-1)",
          }}
        >
          <p className="text-[14px] text-[color:var(--color-fg-muted)]">
            Print sizes still rendering — refresh in a minute.
          </p>
        </div>
        <div className="md:col-span-5">
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(40px, 5.6vw, 88px)",
              lineHeight: 0.94,
              letterSpacing: "-0.035em",
            }}
          >
            {title}
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-12 md:grid-cols-12 md:gap-16">
      {/* HERO — swaps when a size is picked */}
      <div className="md:col-span-7">
        <div
          className="relative overflow-hidden rounded-2xl"
          style={{
            aspectRatio: activeInfo?.aspect ?? "4 / 5",
            border: "1px solid var(--color-border-default)",
            background: "var(--color-bg-elev-1)",
            transition: "aspect-ratio 320ms",
          }}
        >
          {active.cover ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={active.cover}
                alt={`${title} — ${activeInfo?.label ?? active.ratio}`}
                draggable={false}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="absolute inset-0 h-full w-full select-none object-cover"
              />
              {/* Watermark overlay — visual signal that the displayed
               * image is the preview, not the deliverable file. Backend
               * stores hero_image_url un-watermarked (it's the print
               * file the paid buyer gets after webhook delivery), and
               * watermarked_preview_url is null on most live rows
               * today, so PDP falls back to the un-watermarked hero.
               *
               * 2026-06-01 Sagar QA: "in normal wall art there should
               * be watermark but there is no watermark."
               *
               * CSS overlay isn't a real file-level watermark — anyone
               * who right-clicks → save still gets the original — but
               * it stops casual screenshot piracy AND visually signals
               * "this is a paid product" to honest buyers. The real
               * fix is backend serving watermarked image variants
               * (relayed to backend Claude as separate ask). */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "repeating-linear-gradient(-30deg, transparent 0 110px, rgba(255,255,255,0.04) 110px 130px)",
                  mixBlendMode: "overlay",
                }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
              >
                <div
                  className="font-mono uppercase"
                  style={{
                    transform: "rotate(-22deg)",
                    fontSize: "clamp(28px, 4.5vw, 64px)",
                    fontWeight: 700,
                    letterSpacing: "0.18em",
                    color: "rgba(255,255,255,0.18)",
                    textShadow:
                      "0 1px 2px rgba(0,0,0,0.4), 0 -1px 0 rgba(255,255,255,0.05)",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Apex&nbsp;·&nbsp;Preview&nbsp;·&nbsp;Apex
                </div>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 grid place-items-center font-mono text-[10px] uppercase tracking-[0.16em] text-white">
              rendering
            </div>
          )}
        </div>

        {sorted.length > 1 && (
          <p
            className="mt-3 font-mono text-[11px] uppercase"
            style={{
              color: "var(--color-fg-subtle)",
              letterSpacing: "0.14em",
            }}
          >
            {sorted.length} sizes available · pick one below
          </p>
        )}
      </div>

      {/* RIGHT — title + size selector + email + buy.
       *
       * Title sized to read (Brian QA pass: 88px-uppercase-spilling read
       * as a magazine cover, not a buy block). Description sits under
       * the title as a normal paragraph. */}
      <div className="md:col-span-5">
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(32px, 4.2vw, 56px)",
            fontWeight: 400,
            lineHeight: 1.02,
            letterSpacing: "-0.025em",
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            className="mt-4 max-w-[52ch] text-[16px] leading-[1.55]"
            style={{ color: "var(--color-fg-muted)" }}
          >
            {subtitle}
          </p>
        )}

        {/* Size selector */}
        {sorted.length > 1 && (
          <div className="mt-8">
            <span
              className="mb-3 block font-mono text-[10px] uppercase"
              style={{
                color: "var(--color-fg-subtle)",
                letterSpacing: "0.16em",
              }}
            >
              Choose your size
            </span>
            <div className="flex flex-wrap gap-2">
              {sorted.map((s) => {
                const info = ASPECT_LABELS[s.ratio];
                if (!info) return null;
                const selected = s.slug === active.slug;
                return (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => setActiveSlug(s.slug)}
                    data-interactive
                    className="rounded-lg border px-3 py-2 text-left transition-colors"
                    style={{
                      borderColor: selected
                        ? "rgba(184,115,51,0.55)"
                        : "var(--color-border-default)",
                      background: selected
                        ? "rgba(184,115,51,0.12)"
                        : "rgba(255,255,255,0.02)",
                    }}
                  >
                    <div
                      className="font-mono text-[10px] uppercase"
                      style={{
                        color: selected
                          ? "var(--color-accent)"
                          : "var(--color-fg-subtle)",
                        letterSpacing: "0.14em",
                      }}
                    >
                      {info.label}
                    </div>
                    <div className="mt-1 text-[13px]">{info.printSize}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Price — anchor pricing per Brian QA. */}
        <div className="mt-8">
          <PriceWithAnchor
            priceCents={active.priceCents}
            slug={active.slug}
            variant="hero"
          />
        </div>

        {/* Email + Buy */}
        <div className="mt-8 space-y-3">
          <label className="block">
            <span
              className="mb-2 block font-mono text-[10px] uppercase"
              style={{
                color: "var(--color-fg-subtle)",
                letterSpacing: "0.16em",
              }}
            >
              Where do we send the print?
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@domain.com"
              autoComplete="email"
              className="w-full rounded-lg border bg-black/40 px-4 py-3 text-[15px] outline-none focus:border-[#1E64FF]/40"
              style={{
                borderColor: "rgba(255,255,255,0.08)",
                color: "var(--color-fg)",
              }}
            />
          </label>
          {pass.active ? (
            /* Member already — skip checkout, point at library. The
             * webhook delivered the high-res file already; this is just
             * a faster path for repeat-visit members. */
            <a
              href="/account/library"
              data-interactive
              className="inline-flex w-full items-center justify-center rounded-lg px-5 py-3 text-[15px] font-medium"
              style={{
                background: "rgba(217,163,90,0.15)",
                border: "1px solid rgba(217,163,90,0.5)",
                color: "var(--color-accent)",
              }}
            >
              ✓ Already in your library — open it →
            </a>
          ) : (
            <Cta variant="primary" onClick={onBuy} disabled={busy} full>
              {busy
                ? "Opening checkout…"
                : `Buy with Stripe — $${(active.priceCents / 100).toFixed(0)}`}
            </Cta>
          )}
          <MemberPrice usd={active.priceCents / 100} />
          {!pass.active && (
            <p className="text-[12px] leading-[1.5] text-white/65">
              Print credit: every dollar of this comes off a framed, canvas or poster print of the same design for 90 days. The code arrives with your file.
            </p>
          )}
          {error && (
            <p className="text-[13px]" style={{ color: "#ef4444" }}>
              {error}
            </p>
          )}
          <p
            className="font-mono text-[10px] uppercase"
            style={{
              color: "var(--color-fg-subtle)",
              letterSpacing: "0.14em",
            }}
          >
            ↳ High-res print file emailed within 60s of payment · 14-day
            refund
          </p>
        </div>
      </div>

      <CrossSellModal
        open={showCrossSell}
        primaryLabel={activeInfo?.label ?? title}
        primaryPriceCents={active.priceCents}
        bump={bumpSibling}
        onAccept={onAcceptBundle}
        onDecline={onDeclineBundle}
        busy={busy}
      />
      <CheckoutDisclosure className="mt-3" />
    </div>
  );
}
